import {
	Background,
	ControlButton,
	Controls,
	type Edge as FlowEdge,
	type Node as FlowNode,
	MiniMap,
	ReactFlow,
	type ReactFlowInstance,
	useEdgesState,
	useNodesState
} from "@xyflow/react";
import type { EntityDto, LinkedTreeDto, TreeRelationDto } from "api/types";
import { Spinner } from "components";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getTreeViewPath } from "routes/Paths";
import { type RelationGroupId, relationGroupOf } from "utilities";
import { formatRelationType } from "utilities/relationFormatter";

import EdgePublisher from "./EdgePublisher";
import FloatingEdge from "./FloatingEdge";
import { computeForcePositions } from "./forceLayout";
import GraphNodeItem from "./GraphNodeItem";
import { assignLevels, buildPeerPairs } from "./levelLayout";
import LinkedTreeNode from "./LinkedTreeNode";
import MiniMapNode from "./MiniMapNode";
import { findNearbySeat } from "./nearbySeat";
import { computeRadialPositions, pickDefaultCenter } from "./radialLayout";
import {
	computeRingSeats,
	resolveRingOwner,
	type RingObstacle,
	type RingSeat,
	type Satellite,
	splitEntities
} from "./radialReveal";
import RelationBadgeEdge from "./RelationBadgeEdge";
import RingGuideEdge from "./RingGuideEdge";
import SatelliteNode from "./SatelliteNode";
import {
	GAP_X,
	LEVEL_HEIGHT,
	MOVE_HISTORY_LIMIT,
	NODE_HEIGHT,
	NODE_WIDTH,
	PADDING,
	SATELLITE_BADGE,
	SATELLITE_WIDTH
} from "./treeGraph.constants";
import type {
	BadgedEdge,
	CanvasPositionsSnapshot,
	Edge,
	EntityNodeData,
	LinkedTreeNodeData,
	NewEntitySeat,
	NodeSeat,
	RelationBadgeKind,
	SatelliteNodeData,
	ThreadEdge,
	TreeGraphHandle,
	TreeGraphProps,
	TreeOutlineEntry
} from "./treeGraph.types";
import TreeOutline from "./TreeOutline";
import UnionEdge from "./UnionEdge";

import "@xyflow/react/dist/style.css";
import styles from "./TreeGraph.module.css";

// Relation types where the "from" endpoint is the elder (parent/ancestor) of the "to" endpoint.
const DOWN_TYPES = new Set(["Father", "Mother", "Grandfather", "Grandmother"]);
// Relation types where the "from" endpoint is the descendant of the "to" endpoint.
const UP_TYPES = new Set(["Son", "Daughter", "Grandson", "Granddaughter"]);

/**
 * Relation kinds that render a midpoint badge (both halves of a mirrored pair map to the same glyph).
 * Former (ex) relations always fall back to a plain dashed thread.
 */
const BADGE_KINDS: Record<string, RelationBadgeKind> = {
	Husband: "marriage",
	Wife: "marriage",
	LoveInterest: "love",
	Friend: "friend",
	Brother: "sibling",
	Sister: "sibling",
	Enemy: "enemy",
	Rival: "rival",
	BondedTo: "bond",
	BondedWith: "bond",
	CompanionOf: "bond",
	HasCompanion: "bond",
	MemberOf: "team",
	HasMember: "team",
	LedBy: "team",
	LeaderOf: "team",
	BelongsToPack: "team",
	HasPackMember: "team",
	AlliedWith: "team",
	PredecessorOf: "succession",
	SuccessorOf: "succession"
};

/** Stable empty default for the optional seats prop, so the layout memo is not rebuilt every render. */
const NO_NEW_SEATS: NewEntitySeat[] = [];

const NODE_TYPES = { entity: GraphNodeItem, linkedTree: LinkedTreeNode, satellite: SatelliteNode };
const EDGE_TYPES = { badge: RelationBadgeEdge, floating: FloatingEdge, ringGuide: RingGuideEdge, union: UnionEdge };

type CanvasNode = FlowNode<EntityNodeData> | FlowNode<LinkedTreeNodeData> | FlowNode<SatelliteNodeData>;

/** Node id of a ring chip: the satellite entity's, or the owner's "+N more" chip. */
const satelliteNodeId = (ownerId: string, entityId: string | null): string =>
	entityId === null ? `sat-more-${ownerId}` : `sat-${entityId}`;

/** Hover text for a relation row, read in the stored direction: "A is Father of B", "A is Member Of B". */
const relationTooltip = (relation: TreeRelationDto, titleById: Map<string, string>): string => {
	const from = titleById.get(relation.fromEntityId) ?? "";
	const to = titleById.get(relation.toEntityId) ?? "";
	const label = formatRelationType(relation.relationType, relation.isEx);
	// Types that already end in their own preposition read as "is Led By Aang"; only the bare ones
	// ("Father", "Friend") need "of" added, or you get "is Belongs To of Aang".
	const connector = /(?:Of|With|To|By|At|In|For)$/.test(relation.relationType) ? "" : " of";

	return `${from} is ${label}${connector} ${to}`;
};

/**
 * Collapse mirrored relation pairs into directed parent→child edges where the type implies a generation,
 * and into undirected "thread" edges (spouses, siblings, ownership, etc.) for everything else.
 */
const buildEdges = (
	relations: TreeRelationDto[],
	titleById: Map<string, string>
): { hierarchical: Edge[]; badged: BadgedEdge[]; threads: ThreadEdge[]; leveling: Edge[] } => {
	const seenPairs = new Set<string>();
	const hierarchical: Edge[] = [];
	const badged: BadgedEdge[] = [];
	const threads: ThreadEdge[] = [];
	// Edges that push the "child" a generation below the "parent" in the auto-layout:
	// real parent/child relations plus succession (a predecessor sits one level above their successor).
	const leveling: Edge[] = [];
	// Direct parent → children, from Father/Mother/Son/Daughter rows only (grandparent types are
	// excluded on purpose: grandchildren of one grandparent are cousins, not siblings).
	const childrenByParent = new Map<string, string[]>();
	const addChild = (parentId: string, childId: string): void => {
		if (!childrenByParent.has(parentId)) {
			childrenByParent.set(parentId, []);
		}
		childrenByParent.get(parentId)!.push(childId);
	};

	relations.forEach((relation: TreeRelationDto) => {
		if (seenPairs.has(relation.pairId)) {
			return;
		}

		// Every edge carries the context it belongs to. Only the drawn edges are filtered by it — the
		// leveling pairs below always count, so generation rows are the same whichever context is shown.
		const group = relationGroupOf(relation.relationType);
		const badgeKind = BADGE_KINDS[relation.relationType];
		if (DOWN_TYPES.has(relation.relationType)) {
			hierarchical.push({
				parentId: relation.fromEntityId,
				childId: relation.toEntityId,
				tooltip: relationTooltip(relation, titleById),
				group
			});
			leveling.push({ parentId: relation.fromEntityId, childId: relation.toEntityId, group });
			if (relation.relationType === "Father" || relation.relationType === "Mother") {
				addChild(relation.fromEntityId, relation.toEntityId);
			}
			seenPairs.add(relation.pairId);
		} else if (UP_TYPES.has(relation.relationType)) {
			hierarchical.push({
				parentId: relation.toEntityId,
				childId: relation.fromEntityId,
				tooltip: relationTooltip(relation, titleById),
				group
			});
			leveling.push({ parentId: relation.toEntityId, childId: relation.fromEntityId, group });
			if (relation.relationType === "Son" || relation.relationType === "Daughter") {
				addChild(relation.toEntityId, relation.fromEntityId);
			}
			seenPairs.add(relation.pairId);
		} else if (badgeKind && !relation.isEx) {
			if (relation.relationType === "PredecessorOf") {
				leveling.push({ parentId: relation.fromEntityId, childId: relation.toEntityId, group });
			} else if (relation.relationType === "SuccessorOf") {
				leveling.push({ parentId: relation.toEntityId, childId: relation.fromEntityId, group });
			}

			badged.push({
				aId: relation.fromEntityId,
				bId: relation.toEntityId,
				pairId: relation.pairId,
				kind: badgeKind,
				tooltip: relationTooltip(relation, titleById),
				group
			});
			seenPairs.add(relation.pairId);
		}
	});

	relations.forEach((relation: TreeRelationDto) => {
		if (seenPairs.has(relation.pairId)) {
			return;
		}

		threads.push({
			aId: relation.fromEntityId,
			bId: relation.toEntityId,
			pairId: relation.pairId,
			tooltip: relationTooltip(relation, titleById),
			group: relationGroupOf(relation.relationType)
		});
		seenPairs.add(relation.pairId);
	});

	// Derived family relations: facts implied by the recorded parent/child and sibling rows are
	// surfaced on the canvas without writing anything to the tree. Each derived connection renders
	// exactly like its explicit counterpart (sibling badge, dashed uncle/cousin thread) and always
	// yields to an explicit relation of any kind between the pair. They carry derived: true so the
	// canvas can keep them out of the resting view - on a large tree they outnumber the recorded
	// relations several times over and bury the lineage the graph exists to show.
	const pairKey = (a: string, b: string): string => (a < b ? `${a}|${b}` : `${b}|${a}`);
	const explicitPairs = new Set(
		relations.map((relation: TreeRelationDto) => pairKey(relation.fromEntityId, relation.toEntityId))
	);
	const derivedPairs = new Set<string>();
	const canDerive = (a: string, b: string): boolean =>
		a !== b && !explicitPairs.has(pairKey(a, b)) && !derivedPairs.has(pairKey(a, b));

	const parentsByChild = new Map<string, string[]>();
	childrenByParent.forEach((children: string[], parentId: string) => {
		children.forEach((childId: string) => {
			if (!parentsByChild.has(childId)) {
				parentsByChild.set(childId, []);
			}
			parentsByChild.get(childId)!.push(parentId);
		});
	});

	// Sibling adjacency drives the uncle/cousin steps: recorded Brother/Sister rows plus the
	// derived shared-parent pairs below.
	const siblingsOf = new Map<string, Set<string>>();
	const linkSiblings = (a: string, b: string): void => {
		if (!siblingsOf.has(a)) {
			siblingsOf.set(a, new Set());
		}
		if (!siblingsOf.has(b)) {
			siblingsOf.set(b, new Set());
		}
		siblingsOf.get(a)!.add(b);
		siblingsOf.get(b)!.add(a);
	};
	relations.forEach((relation: TreeRelationDto) => {
		if (relation.relationType === "Brother" || relation.relationType === "Sister") {
			linkSiblings(relation.fromEntityId, relation.toEntityId);
		}
	});

	const isParentOf = (parentId: string, childId: string): boolean =>
		(parentsByChild.get(childId) ?? []).includes(parentId);

	// Siblings: children who share a parent.
	childrenByParent.forEach((children: string[]) => {
		for (let i = 0; i < children.length; i += 1) {
			for (let j = i + 1; j < children.length; j += 1) {
				const a = children[i];
				const b = children[j];
				// A node that parented one of its co-children is not that child's sibling. It happens
				// wherever a primordial bore both a partner and their shared offspring - Gaia bore
				// Uranus, then the Titans by him, which makes Uranus a co-child of Gaia alongside his
				// own sons. The badge was always suppressed here (canDerive sees the explicit
				// parent row), but siblingsOf is not guarded, so the false pair used to leak into the
				// uncle/aunt and cousin steps below and drew Uranus as uncle of his own grandchildren.
				if (isParentOf(a, b) || isParentOf(b, a)) {
					continue;
				}

				linkSiblings(a, b);
				if (canDerive(a, b)) {
					derivedPairs.add(pairKey(a, b));
					badged.push({
						aId: a,
						bId: b,
						pairId: `sib-${pairKey(a, b)}`,
						kind: "sibling",
						tooltip: `${titleById.get(a)} & ${titleById.get(b)} — Siblings`,
						derived: true,
						group: "family"
					});
				}
			}
		}
	});

	// Grandparents are intentionally NOT derived: the parent→child chain already implies the
	// two-generation link, so an extra long grandparent line would only add clutter. Explicit
	// Grandfather/Grandmother/Grandson/Granddaughter rows still render via DOWN_TYPES/UP_TYPES.

	// Uncles/aunts: a sibling of a parent (explicit or derived), rendered like an explicit
	// Uncle/Aunt row (plain dashed thread). Skips anyone who is also a parent of the child.
	parentsByChild.forEach((parents: string[], childId: string) => {
		parents.forEach((parentId: string) => {
			(siblingsOf.get(parentId) ?? new Set<string>()).forEach((auntOrUncleId: string) => {
				if (!parents.includes(auntOrUncleId) && canDerive(auntOrUncleId, childId)) {
					derivedPairs.add(pairKey(auntOrUncleId, childId));
					threads.push({
						aId: auntOrUncleId,
						bId: childId,
						pairId: `unc-${pairKey(auntOrUncleId, childId)}`,
						tooltip: `${titleById.get(auntOrUncleId)} is Uncle/Aunt of ${titleById.get(childId)}`,
						derived: true,
						group: "family"
					});
				}
			});
		});
	});

	// Cousins: children of siblings, rendered like an explicit Cousin row (plain dashed thread).
	siblingsOf.forEach((siblings: Set<string>, aId: string) => {
		siblings.forEach((bId: string) => {
			if (aId < bId) {
				(childrenByParent.get(aId) ?? []).forEach((childA: string) => {
					(childrenByParent.get(bId) ?? []).forEach((childB: string) => {
						if (canDerive(childA, childB)) {
							derivedPairs.add(pairKey(childA, childB));
							threads.push({
								aId: childA,
								bId: childB,
								pairId: `cuz-${pairKey(childA, childB)}`,
								tooltip: `${titleById.get(childA)} & ${titleById.get(childB)} — Cousins`,
								derived: true,
								group: "family"
							});
						}
					});
				});
			}
		});
	});

	return { hierarchical, badged, threads, leveling };
};

/** Bidirectional adjacency over every edge kind, used to seat related nodes next to each other. */
const buildAdjacency = (hierarchical: Edge[], threads: ThreadEdge[]): Map<string, string[]> => {
	const adjacency = new Map<string, string[]>();
	const link = (a: string, b: string): void => {
		if (!adjacency.has(a)) {
			adjacency.set(a, []);
		}
		adjacency.get(a)!.push(b);
	};

	hierarchical.forEach((edge: Edge) => {
		link(edge.parentId, edge.childId);
		link(edge.childId, edge.parentId);
	});
	threads.forEach((thread: ThreadEdge) => {
		link(thread.aId, thread.bId);
		link(thread.bId, thread.aId);
	});

	return adjacency;
};

/**
 * Order a row so related nodes sit together. Romantic partners (spouses, love interests) anchor next
 * to each other before any other relation is considered, and nobody may be wedged between a seated
 * couple, so friendships can no longer split a marriage. Non-person companions (creatures, objects)
 * attach on the outer side of their partner instead.
 */
const reorderRow = (
	row: EntityDto[],
	adjacency: Map<string, string[]>,
	romantic: Map<string, string[]>
): EntityDto[] => {
	const ordered: EntityDto[] = [];
	const position = new Map<string, number>();
	const isCouple = (a: EntityDto, b: EntityDto): boolean => (romantic.get(a.id) ?? []).includes(b.id);

	row.forEach((entity: EntityDto) => {
		const placedAt = (ids: string[]): number[] =>
			ids
				.map((id: string) => position.get(id))
				.filter((index: number | undefined): index is number => index !== undefined);

		const romanticPartners = placedAt(romantic.get(entity.id) ?? []);
		const placedPartners = placedAt(adjacency.get(entity.id) ?? []);
		const anchors = romanticPartners.length > 0 ? romanticPartners : placedPartners;

		let insertAt = ordered.length;
		if (anchors.length > 0) {
			const anchor = Math.min(...anchors);
			const attachLeft = entity.type !== "Person" && anchor < ordered.length / 2;
			insertAt = attachLeft ? anchor : anchor + 1;

			// Slide right past any couple this slot would split (unless the entity belongs to that couple).
			while (
				insertAt > 0 &&
				insertAt < ordered.length &&
				isCouple(ordered[insertAt - 1], ordered[insertAt]) &&
				!isCouple(ordered[insertAt - 1], entity) &&
				!isCouple(ordered[insertAt], entity)
			) {
				insertAt += 1;
			}
		}

		ordered.splice(insertAt, 0, entity);
		ordered.forEach((placed: EntityDto, index: number) => position.set(placed.id, index));
	});

	return ordered;
};

const TreeGraph = forwardRef<TreeGraphHandle, TreeGraphProps>(
	(props: TreeGraphProps, ref: React.ForwardedRef<TreeGraphHandle>) => {
		const {
			entities,
			layoutMode = "hierarchical",
			newEntitySeats = NO_NEW_SEATS,
			relationGroup = "all",
			relations,
			onBackgroundClick,
			onSelectEntity,
			selectedEntityId,
			showMiniMap = true,
			readOnly = true
		} = props;
		const { t } = useTranslation("homePage");
		// Lets the node rebuild read the current translator without taking a dependency on it.
		const tRef = useRef(t);
		tRef.current = t;
		const wrapperRef = useRef<HTMLDivElement>(null);
		const [isMiniMapVisible, setIsMiniMapVisible] = useState(true);
		const toggleMiniMap = useCallback((): void => setIsMiniMapVisible((visible: boolean) => !visible), []);
		// Only structural entities (and orphaned secondary ones) are laid out; the rest wait in the ring
		// of whichever structural entity is selected (TTWEB-34).
		const split = useMemo(() => splitEntities(entities, relations), [entities, relations]);
		const { canvasEntities, canvasRelations } = split;
		// Read by the next resolve, so clicking one chip after another keeps the ring they came from.
		const ringOwnerRef = useRef<string | null>(null);
		const ringOwnerId = useMemo(
			() => resolveRingOwner(selectedEntityId ?? null, ringOwnerRef.current, split, entities),
			[entities, selectedEntityId, split]
		);
		ringOwnerRef.current = ringOwnerId;
		// The owner whose ring has chips in it. While there is one, everything else on the canvas steps
		// back (see dimmedNode), so the ring reads as one group rather than chips strewn over the tree.
		const ringFocusId =
			ringOwnerId !== null && (split.satellitesByOwner.get(ringOwnerId)?.length ?? 0) > 0 ? ringOwnerId : null;
		// Read by the rebuild, which must not re-run on selection: it would throw away dragged positions.
		const ringFocusRef = useRef(ringFocusId);
		ringFocusRef.current = ringFocusId;
		const selectedIdRef = useRef(selectedEntityId);
		selectedIdRef.current = selectedEntityId;
		const isSelectionOnCanvas =
			selectedEntityId != null && canvasEntities.some((entity: EntityDto) => entity.id === selectedEntityId);
		// Stays null in hierarchical mode so selection clicks never rebuild nodes there (dragged positions survive).
		// Radial mode centres on the ring's owner, so picking a chip never re-centres on a node that is not drawn.
		let radialCenterId: string | null = null;
		if (layoutMode === "radial") {
			radialCenterId = ringOwnerId ?? (isSelectionOnCanvas ? selectedEntityId ?? null : null);
		}
		// Hierarchical mode builds generation rows with related nodes seated together and organizations floated
		// as hubs above their members, then lets explicitly saved positions (Save Layout) override the computed
		// seats; radial mode arranges everything on concentric rings around the selected (or most-connected)
		// entity, and force mode clusters by connectivity — both always start from their auto-layout.
		const layout = useMemo(() => {
			const titleById = new Map<string, string>(
				canvasEntities.map((entity: EntityDto) => [entity.id, entity.title])
			);
			const { hierarchical, badged, threads, leveling } = buildEdges(canvasRelations, titleById);
			const adjacency = buildAdjacency(hierarchical, [...badged, ...threads]);

			// Romantic links (marriage, love interest) get seating priority over friendships and the like.
			const romantic = new Map<string, string[]>();
			const linkRomantic = (a: string, b: string): void => {
				if (!romantic.has(a)) {
					romantic.set(a, []);
				}
				romantic.get(a)!.push(b);
			};
			badged
				.filter((badge: BadgedEdge) => badge.kind === "marriage" || badge.kind === "love")
				.forEach((badge: BadgedEdge) => {
					linkRomantic(badge.aId, badge.bId);
					linkRomantic(badge.bId, badge.aId);
				});

			// Radial mode centers on the selected entity, falling back to the most-connected one when the
			// selection is empty or stale (e.g. a deleted entity still referenced by the URL).
			let radialCenter: string | null = null;
			if (layoutMode === "radial") {
				radialCenter = canvasEntities.some((entity: EntityDto) => entity.id === radialCenterId)
					? radialCenterId
					: pickDefaultCenter(canvasEntities, canvasRelations);
			}

			// Generations drive every mode: hierarchical stacks them into rows, radial puts the center's
			// ancestors above it and its descendants below, and force pins each row's height. Adjacency
			// goes in too: it is what seats an entity with no recorded ancestry — a place, an object, an
			// event — on the row of whatever it connects to.
			const level = assignLevels(canvasEntities, leveling, buildPeerPairs(canvasRelations), adjacency);

			const radial =
				radialCenter !== null
					? computeRadialPositions(canvasEntities, adjacency, radialCenter, romantic, level)
					: null;
			const isForceLayout = layoutMode === "force";
			let positions: Map<string, { x: number; y: number }>;
			if (radial !== null) {
				positions = radial.positions;
			} else if (isForceLayout) {
				positions = computeForcePositions(canvasEntities, adjacency, level);
			} else {
				positions = new Map<string, { x: number; y: number }>();
			}

			const collides = (x: number, y: number): boolean =>
				Array.from(positions.values()).some(
					(p: { x: number; y: number }) => Math.abs(p.x - x) < NODE_WIDTH && Math.abs(p.y - y) < NODE_HEIGHT
				);

			if (radialCenter === null && !isForceLayout) {
				const hubs = canvasEntities.filter((entity: EntityDto) => entity.type === "Organization");
				const rowEntities = canvasEntities.filter((entity: EntityDto) => entity.type !== "Organization");

				const byLevel = new Map<number, EntityDto[]>();
				rowEntities.forEach((entity: EntityDto) => {
					const lvl = level.get(entity.id) ?? 0;
					if (!byLevel.has(lvl)) {
						byLevel.set(lvl, []);
					}
					byLevel.get(lvl)!.push(entity);
				});

				const levels = Array.from(byLevel.keys()).sort((a: number, b: number) => a - b);
				const widestCount = levels.reduce(
					(max: number, lvl: number) => Math.max(max, byLevel.get(lvl)!.length),
					1
				);
				const rowWidth = widestCount * NODE_WIDTH + (widestCount - 1) * GAP_X;

				levels.forEach((lvl: number, rowIndex: number) => {
					const row = reorderRow(byLevel.get(lvl)!, adjacency, romantic);
					const thisRowWidth = row.length * NODE_WIDTH + (row.length - 1) * GAP_X;
					const startX = PADDING + (rowWidth - thisRowWidth) / 2;
					row.forEach((entity: EntityDto, colIndex: number) => {
						positions.set(entity.id, {
							x: startX + colIndex * (NODE_WIDTH + GAP_X),
							y: PADDING + rowIndex * LEVEL_HEIGHT
						});
					});
				});

				// Organizations float as hubs: centered above the members/allies they connect to.
				hubs.forEach((org: EntityDto, index: number) => {
					const partnerPositions = (adjacency.get(org.id) ?? [])
						.map((id: string) => positions.get(id))
						.filter(
							(p: { x: number; y: number } | undefined): p is { x: number; y: number } => p !== undefined
						);

					if (partnerPositions.length === 0) {
						positions.set(org.id, { x: PADDING + index * (NODE_WIDTH + GAP_X), y: PADDING - LEVEL_HEIGHT });

						return;
					}

					const avgX =
						partnerPositions.reduce((sum: number, p: { x: number; y: number }) => sum + p.x, 0) /
						partnerPositions.length;
					const minY = Math.min(...partnerPositions.map((p: { x: number; y: number }) => p.y));

					let x = avgX;
					const y = minY - LEVEL_HEIGHT;
					while (collides(x, y)) {
						x += NODE_WIDTH + GAP_X;
					}

					positions.set(org.id, { x, y });
				});

				// Saved positions win over the computed rows; entities never saved keep their auto seat.
				const savedIds = new Set<string>();
				canvasEntities.forEach((entity: EntityDto) => {
					if (entity.positionX != null && entity.positionY != null) {
						savedIds.add(entity.id);
						positions.set(entity.id, { x: entity.positionX, y: entity.positionY });
					}
				});

				// A node created in this session is seated beside whatever was last worked on, instead
				// of wherever the rows would otherwise drop it. Both ways they drop it are off screen:
				// the seating only ever anchors a node to its neighbours within its own row, so a new
				// node - which has nobody there yet - goes on the end of that row, which on a wide tree
				// is past the right edge of the frame; and on a tree with a saved layout the computed
				// rows are nowhere near the seats the user actually arranged.
				//
				// Only the column follows the anchor. The row does not: a new child belongs under its
				// parents, not alongside them, so anything the tree records a relation for keeps the
				// generation row the layout gave it, and only a node with no relations at all - which
				// has no generation to keep, the layout having defaulted it to the top row - takes the
				// anchor's own row. A row is a contiguous run of nodes, so on a dense tree the spot
				// under the anchor is taken and the search settles a row further out; a node one row
				// off but next to the work in progress still reads far better than a correctly-seated
				// one parked past the right edge, and it is one drag from where the user wants it.
				//
				// Seats are applied in creation order, so two nodes added one after the other stack up
				// beside the anchor rather than on top of each other. A position the user explicitly
				// saved still wins: once a new node has been placed and saved it is no longer new, and
				// nothing here moves it again.
				newEntitySeats.forEach((seat: NewEntitySeat) => {
					const anchor = positions.get(seat.anchorId);
					const current = positions.get(seat.entityId);
					if (anchor === undefined || current === undefined || savedIds.has(seat.entityId)) {
						return;
					}

					const origin = { x: anchor.x, y: adjacency.has(seat.entityId) ? current.y : anchor.y };
					// Off the canvas while the free spot is chosen, so the node's own auto seat cannot
					// be mistaken for something to avoid.
					positions.delete(seat.entityId);
					positions.set(seat.entityId, findNearbySeat(origin, collides));
				});
			}

			// Linked-tree nodes sit to the right of their owning entity.
			const linkedTreePositions = new Map<string, { x: number; y: number }>();
			const linkedTreeCollides = (x: number, y: number): boolean =>
				collides(x, y) ||
				Array.from(linkedTreePositions.values()).some(
					(p: { x: number; y: number }) => Math.abs(p.x - x) < NODE_WIDTH && Math.abs(p.y - y) < NODE_HEIGHT
				);

			canvasEntities.forEach((entity: EntityDto) => {
				(entity.linkedTrees ?? []).forEach((link: LinkedTreeDto) => {
					// Saved positions win in hierarchical mode, same as entity nodes.
					if (radialCenter === null && !isForceLayout && link.positionX != null && link.positionY != null) {
						linkedTreePositions.set(link.id, { x: link.positionX, y: link.positionY });

						return;
					}

					const owner = positions.get(entity.id) ?? { x: PADDING, y: PADDING };
					let x = owner.x + NODE_WIDTH + GAP_X * 2;
					const y = owner.y;
					while (linkedTreeCollides(x, y)) {
						x += NODE_WIDTH + GAP_X;
					}

					linkedTreePositions.set(link.id, { x, y });
				});
			});

			return {
				hierarchical,
				badged,
				threads,
				positions,
				linkedTreePositions,
				radialCenter,
				radialGuides: radial?.guides ?? [],
				// Keyboard traversal reads these: generation levels plus every relation, not just the
				// parent/child ones. A tree of friendships and memberships has no Father/Mother rows
				// at all, and would otherwise have nothing to walk.
				adjacency,
				level
			};
		}, [canvasEntities, canvasRelations, layoutMode, newEntitySeats, radialCenterId]);

		const [nodes, setNodes, onNodesChange] = useNodesState<CanvasNode>([]);
		const [edges, setEdges, onEdgesChange] = useEdgesState<FlowEdge>([]);
		const [flowInstance, setFlowInstance] = useState<ReactFlowInstance<CanvasNode, FlowEdge> | null>(null);

		/**
		 * Drag history for the canvas undo: one entry per completed drag, holding where the moved nodes
		 * sat before it. Positions only — nothing here can add or remove an entity or a relation.
		 */
		const moveHistoryRef = useRef<NodeSeat[][]>([]);
		/** Seats captured at drag start, promoted into the history once the drag actually lands. */
		const dragOriginRef = useRef<NodeSeat[]>([]);

		const onNodeDragStart = useCallback(
			(_event: MouseEvent | TouchEvent, _node: CanvasNode, dragged: CanvasNode[]): void => {
				dragOriginRef.current = dragged.map((node: CanvasNode) => ({
					id: node.id,
					x: node.position.x,
					y: node.position.y
				}));
			},
			[]
		);

		const onNodeDragStop = useCallback(
			(_event: MouseEvent | TouchEvent, _node: CanvasNode, dragged: CanvasNode[]): void => {
				const origin = dragOriginRef.current;
				dragOriginRef.current = [];

				// A click on a node also starts and stops a drag; only a real move is worth undoing.
				const moved = origin.filter((seat: NodeSeat) =>
					dragged.some(
						(node: CanvasNode) =>
							node.id === seat.id && (node.position.x !== seat.x || node.position.y !== seat.y)
					)
				);
				if (moved.length === 0) {
					return;
				}

				moveHistoryRef.current = [...moveHistoryRef.current, moved].slice(-MOVE_HISTORY_LIMIT);
			},
			[]
		);

		const undoLastMove = useCallback((): void => {
			const history = moveHistoryRef.current;
			const present = new Set(nodes.map((node: CanvasNode) => node.id));

			// An entry goes stale when every node it moved has since been deleted; walk past those
			// rather than spending the keystroke on an undo the user cannot see.
			let index = history.length - 1;
			while (index >= 0 && !history[index].some((seat: NodeSeat) => present.has(seat.id))) {
				index--;
			}
			if (index < 0) {
				moveHistoryRef.current = [];

				return;
			}

			const seats = new Map(history[index].map((seat: NodeSeat) => [seat.id, seat]));
			moveHistoryRef.current = history.slice(0, index);

			setNodes((current: CanvasNode[]) =>
				current.map((node: CanvasNode) => {
					const seat = seats.get(node.id);

					return seat === undefined ? node : { ...node, position: { x: seat.x, y: seat.y } };
				})
			);
		}, [nodes, setNodes]);

		// Lets the page snapshot where every entity currently sits (the explicit Save Layout action)
		// and walk back the drags made since the canvas was last built.
		useImperativeHandle(
			ref,
			() => ({
				getCanvasPositions: (): CanvasPositionsSnapshot => ({
					entities: nodes
						.filter((node: CanvasNode) => node.type === "entity")
						.map((node: CanvasNode) => ({ entityId: node.id, x: node.position.x, y: node.position.y })),
					links: nodes
						.filter((node: CanvasNode) => node.type === "linkedTree")
						.map((node: CanvasNode) => ({
							linkId: node.id.replace("treelink-", ""),
							x: node.position.x,
							y: node.position.y
						}))
				}),
				undoLastMove
			}),
			[nodes, undoLastMove]
		);

		/**
		 * Whether React Flow has drawn the canvas yet, for the loading cover. Laying out a large tree
		 * takes long enough to look broken - nodes are mounted but unmeasured, so the canvas is blank
		 * with no connectors - and the first edge publish (see EdgePublisher) is the moment it is
		 * genuinely painted.
		 */
		const [isCanvasReady, setIsCanvasReady] = useState(false);

		// A fresh array every time: React Flow reconciles edges by identity, and handing back the
		// same one after it has dropped an edge internally does not bring that edge back.
		const onPublishEdges = useCallback(
			(next: FlowEdge[]): void => {
				setEdges([...next]);
				setIsCanvasReady(true);
			},
			[setEdges]
		);

		/**
		 * Rebuild the canvas when the tree data changes (unsaved drags reset to the saved/auto layout).
		 *
		 * `t` is read through a ref rather than listed as a dependency. The homePage namespace is not
		 * one of the three fetched at boot, so with `useSuspense: false` it arrives over HTTP some
		 * time after this component mounts and hands back a new `t`. Keyed on that, this effect
		 * rebuilt every node at a moment decided by the network - often enough mid-measurement that
		 * React Flow discarded the edges and never reconsidered them. Labels catch up in the effect
		 * below, which touches nothing but the label.
		 *
		 * A node that survives the rebuild keeps the size React Flow measured on it. Every node here has
		 * a fixed size, and dropping the measurement hid the whole canvas until React Flow measured
		 * again - which it never does when the rebuild lands in the same render as a measurement,
		 * because it only re-measures a node it sees go from measured to unmeasured. That left an empty
		 * canvas after adding an entity, until a reload. Only nodes new to the canvas wait to be measured.
		 */
		useEffect(() => {
			const dimmed = (id: string): string | undefined =>
				ringFocusRef.current !== null && id !== ringFocusRef.current ? styles.dimmedNode : undefined;

			// Without an explicit ariaLabel React Flow leaves the node's group unnamed, so a screen
			// reader announces position and role but never says which entity it landed on.
			const entityNodes: CanvasNode[] = canvasEntities.map((entity: EntityDto) => ({
				id: entity.id,
				type: "entity",
				position: layout.positions.get(entity.id) ?? { x: PADDING, y: PADDING },
				ariaLabel: tRef.current("view.canvasNodeLabel", { title: entity.title, type: entity.type }),
				className: dimmed(entity.id),
				data: { entity, isSelected: entity.id === selectedIdRef.current }
			}));

			const linkedTreeNodes: CanvasNode[] = canvasEntities.flatMap((entity: EntityDto) =>
				(entity.linkedTrees ?? []).map((link: LinkedTreeDto) => ({
					id: `treelink-${link.id}`,
					type: "linkedTree",
					position: layout.linkedTreePositions.get(link.id) ?? { x: PADDING, y: PADDING },
					ariaLabel: tRef.current("view.canvasLinkedTreeLabel", { title: link.title }),
					className: dimmed(`treelink-${link.id}`),
					data: { treeId: link.treeId, title: link.title, ownerEntityId: entity.id }
				}))
			);

			const next = [...entityNodes, ...linkedTreeNodes];
			setNodes((current: CanvasNode[]) => {
				const measuredById = new Map(current.map((node: CanvasNode) => [node.id, node.measured]));
				const kept = next.map((node: CanvasNode) => {
					const measured = measuredById.get(node.id);

					return (measured === undefined ? node : { ...node, measured }) as CanvasNode;
				});

				// Ring chips belong to the ring effect, which reseats them if their owner moved.
				return [...kept, ...current.filter((node: CanvasNode) => node.type === "satellite")];
			});
			// eslint-disable-next-line react-hooks/exhaustive-deps -- `t` is read via tRef on purpose; see above.
		}, [canvasEntities, layout, setNodes]);

		/**
		 * The ring open around the selected structural entity: its related secondary entities, seated
		 * around wherever that node sits right now - dragged or not - and following it while it moves.
		 */
		const ringSatellites = useMemo(
			(): Satellite[] => (ringOwnerId === null ? [] : split.satellitesByOwner.get(ringOwnerId) ?? []),
			[ringOwnerId, split]
		);
		const allTitleById = useMemo(
			() => new Map<string, string>(entities.map((entity: EntityDto) => [entity.id, entity.title])),
			[entities]
		);
		const ringOwnerSeat =
			ringOwnerId === null ? undefined : nodes.find((node: CanvasNode) => node.id === ringOwnerId)?.position;
		const ringOwnerX = ringOwnerSeat?.x;
		const ringOwnerY = ringOwnerSeat?.y;
		// The boxes a chip must stay off: every other node where it sits right now. Keyed on a string so
		// the ring is only reseated when something actually moves, not on every change to `nodes`.
		const obstacleKey =
			ringFocusId === null
				? ""
				: nodes
						.filter((node: CanvasNode) => node.type !== "satellite" && node.id !== ringFocusId)
						.map((node: CanvasNode) => `${node.position.x},${node.position.y}`)
						.join(";");
		const ringObstacles = useMemo(
			(): RingObstacle[] =>
				obstacleKey === ""
					? []
					: obstacleKey.split(";").map((pair: string) => {
							const [x, y] = pair.split(",").map(Number);

							return { x, y, width: NODE_WIDTH, height: NODE_HEIGHT };
					  }),
			[obstacleKey]
		);

		useEffect(() => {
			let chips: CanvasNode[] = [];
			if (ringOwnerId !== null && ringOwnerX !== undefined && ringOwnerY !== undefined) {
				const owner = allTitleById.get(ringOwnerId) ?? "";
				const byId = new Map(ringSatellites.map((satellite: Satellite) => [satellite.entity.id, satellite]));
				const { seats, overflow } = computeRingSeats(
					{ x: ringOwnerX + NODE_WIDTH / 2, y: ringOwnerY + NODE_HEIGHT / 2 },
					ringSatellites.map((satellite: Satellite) => satellite.entity.id),
					ringObstacles
				);
				chips = seats.map((seat: RingSeat) => {
					const satellite = seat.id === null ? undefined : byId.get(seat.id);
					const entity = satellite?.entity ?? null;
					const ariaLabel = entity
						? t("view.satelliteLabel", { title: entity.title, type: entity.type, owner })
						: t("view.satelliteMoreLabel", { count: overflow, owner });

					return {
						id: satelliteNodeId(ringOwnerId, seat.id),
						type: "satellite",
						// Centred on its seat by the badge, so the spoke meets the round part, not the label.
						position: { x: seat.x - SATELLITE_WIDTH / 2, y: seat.y - SATELLITE_BADGE / 2 },
						draggable: false,
						zIndex: 10,
						ariaLabel,
						data: {
							entity,
							ownerId: ringOwnerId,
							isSelected: entity !== null && entity.id === selectedEntityId,
							label: entity ? entity.title : t("view.satelliteMore", { count: overflow }),
							tooltip: satellite ? relationTooltip(satellite.relation, allTitleById) : ariaLabel
						}
					};
				});
			}

			setNodes((current: CanvasNode[]) => {
				const previous = new Map(
					current
						.filter((node: CanvasNode) => node.type === "satellite")
						.map((node: CanvasNode) => [node.id, node])
				);
				if (chips.length === 0 && previous.size === 0) {
					return current;
				}

				// A chip that stays keeps what React Flow measured on it; only its seat and data move.
				const merged = chips.map((chip: CanvasNode) => {
					const prior = previous.get(chip.id);

					return (prior === undefined ? chip : { ...prior, ...chip }) as CanvasNode;
				});

				return [...current.filter((node: CanvasNode) => node.type !== "satellite"), ...merged];
			});
		}, [
			allTitleById,
			ringObstacles,
			ringOwnerId,
			ringOwnerX,
			ringOwnerY,
			ringSatellites,
			selectedEntityId,
			setNodes,
			t
		]);

		// Accessible names catch up once the namespace lands. Only ariaLabel changes, so the geometry
		// React Flow measured stays exactly as it was.
		useEffect(() => {
			setNodes((current: CanvasNode[]) =>
				current.map((node: CanvasNode) => {
					// Ring chips are rebuilt with their labels whenever `t` changes; see the ring effect.
					if (node.type === "entity") {
						const { entity } = node.data as EntityNodeData;

						return {
							...node,
							ariaLabel: t("view.canvasNodeLabel", { title: entity.title, type: entity.type })
						};
					}
					if (node.type === "linkedTree") {
						const { title } = node.data as LinkedTreeNodeData;

						return { ...node, ariaLabel: t("view.canvasLinkedTreeLabel", { title }) };
					}

					return node;
				})
			);
		}, [setNodes, t]);

		/**
		 * Switching layout mode, or re-centring the radial rings, reseats every node — drags recorded
		 * against the previous arrangement no longer describe anywhere meaningful, so the undo history
		 * starts over.
		 *
		 * Deliberately not keyed on the entity data: refetching after Save Layout rebuilds the canvas
		 * with the very seats it just saved, and clearing there would make Ctrl+Z dead after every
		 * Ctrl+S. Undoing past a save is normal — it re-arranges the canvas again, ready to be saved.
		 */
		useEffect(() => {
			moveHistoryRef.current = [];
			dragOriginRef.current = [];
		}, [layoutMode, radialCenterId]);

		/** The ring's chip ids in seat order: the spokes' targets, and the order Tab walks them in. */
		const ringChipIds = useMemo((): string[] => {
			if (ringOwnerId === null) {
				return [];
			}

			const { seats } = computeRingSeats(
				{ x: 0, y: 0 },
				ringSatellites.map((satellite: Satellite) => satellite.entity.id)
			);

			return seats.map((seat: RingSeat) => satelliteNodeId(ringOwnerId, seat.id));
		}, [ringOwnerId, ringSatellites]);

		/**
		 * Every connector the canvas can draw, derived ones included. Which of them actually render is
		 * decided below against the hovered/focused/selected node, so revealing a node's derived
		 * relations never rebuilds this list and never disturbs dragged positions.
		 */
		const flowEdges = useMemo((): FlowEdge[] => {
			// The context filter applies here and nowhere else: `layout` is built from every relation, so
			// positions, generation rows and the text outline stay put while only the drawn edges change.
			// A node with nothing in the current context keeps its seat and simply loses its connectors.
			const isDrawn = (group: RelationGroupId): boolean => relationGroup === "all" || group === relationGroup;
			const hierarchical = layout.hierarchical.filter((edge: Edge) => isDrawn(edge.group));
			const badged = layout.badged.filter((badge: BadgedEdge) => isDrawn(badge.group));
			const threads = layout.threads.filter((thread: ThreadEdge) => isDrawn(thread.group));

			// Radial mode draws its decorative ring circles as a self-edge on the center node, placed first
			// so it paints beneath every real connector in the edge layer.
			const ringGuideEdges: FlowEdge[] =
				layout.radialCenter !== null && layout.radialGuides.length > 0
					? [
							{
								id: "ring-guides",
								source: layout.radialCenter,
								target: layout.radialCenter,
								type: "ringGuide",
								focusable: false,
								selectable: false,
								data: { guides: layout.radialGuides }
							}
					  ]
					: [];

			// When both parents of a child are a couple (marriage/love badge), the two parent lines merge
			// into one "union" edge that hangs from the couple's badge midpoint — classic genealogy style.
			const coupleKey = (a: string, b: string): string => (a < b ? `${a}|${b}` : `${b}|${a}`);
			const couples = new Set(
				badged
					.filter((badge: BadgedEdge) => badge.kind === "marriage" || badge.kind === "love")
					.map((badge: BadgedEdge) => coupleKey(badge.aId, badge.bId))
			);

			const parentsByChild = new Map<string, string[]>();
			hierarchical.forEach((edge: Edge) => {
				if (!parentsByChild.has(edge.childId)) {
					parentsByChild.set(edge.childId, []);
				}
				parentsByChild.get(edge.childId)!.push(edge.parentId);
			});

			const titleById = new Map<string, string>(entities.map((entity: EntityDto) => [entity.id, entity.title]));
			const unionEdges: FlowEdge[] = [];
			const mergedParentEdges = new Set<string>();
			parentsByChild.forEach((parents: string[], childId: string) => {
				for (let i = 0; i < parents.length; i += 1) {
					for (let j = i + 1; j < parents.length; j += 1) {
						if (couples.has(coupleKey(parents[i], parents[j]))) {
							unionEdges.push({
								id: `u-${parents[i]}-${parents[j]}-${childId}`,
								source: parents[i],
								target: childId,
								type: "union",
								ariaLabel: `Parents of ${titleById.get(childId)}`,
								data: { coParentId: parents[j], tooltip: `Parents of ${titleById.get(childId)}` }
							});
							mergedParentEdges.add(`${parents[i]}->${childId}`);
							mergedParentEdges.add(`${parents[j]}->${childId}`);

							return;
						}
					}
				}
			});

			// All edges are "floating": they connect node borders along the shortest path wherever nodes are dragged.
			const hierarchicalEdges = hierarchical
				.filter((edge: Edge) => !mergedParentEdges.has(`${edge.parentId}->${edge.childId}`))
				.map((edge: Edge) => ({
					id: `h-${edge.parentId}-${edge.childId}`,
					source: edge.parentId,
					target: edge.childId,
					type: "floating",
					// Edges are keyboard-focusable, and React Flow's default label is a pair of raw
					// snowflake ids. The tooltip already reads "X is Father of Y" - reuse it.
					ariaLabel: edge.tooltip,
					data: { variant: "hierarchical", tooltip: edge.tooltip }
				}));

			const badgedEdges = badged.map((badge: BadgedEdge) => ({
				id: `m-${badge.pairId}`,
				source: badge.aId,
				target: badge.bId,
				type: "badge",
				ariaLabel: badge.tooltip,
				data: { kind: badge.kind, tooltip: badge.tooltip, derived: badge.derived === true }
			}));

			const threadEdges = threads.map((thread: ThreadEdge) => ({
				id: `t-${thread.pairId}`,
				source: thread.aId,
				target: thread.bId,
				type: "floating",
				ariaLabel: thread.tooltip,
				data: { variant: "thread", tooltip: thread.tooltip, derived: thread.derived === true }
			}));

			const linkedTreeEdges = canvasEntities.flatMap((entity: EntityDto) =>
				(entity.linkedTrees ?? []).map((link: LinkedTreeDto) => ({
					id: `tl-${link.id}`,
					source: entity.id,
					target: `treelink-${link.id}`,
					type: "floating",
					ariaLabel: `Linked tree: ${link.title}`,
					data: { variant: "thread", tooltip: `Linked tree: ${link.title}` }
				}))
			);

			// Ring spokes are placement, not relations: every chip gets one, whatever context is drawn.
			const spokeEdges: FlowEdge[] =
				ringOwnerId === null
					? []
					: ringChipIds.map((chipId: string) => ({
							id: `s-${chipId}`,
							source: ringOwnerId,
							target: chipId,
							type: "floating",
							focusable: false,
							selectable: false,
							data: { variant: "spoke" }
					  }));

			return [
				...ringGuideEdges,
				...unionEdges,
				...hierarchicalEdges,
				...badgedEdges,
				...threadEdges,
				// Linked-tree connectors are not relations and belong to no context, so they always draw.
				...linkedTreeEdges,
				...spokeEdges
			];
		}, [canvasEntities, entities, layout, relationGroup, ringChipIds, ringOwnerId]);

		/**
		 * The node currently under the pointer or holding keyboard focus. Derived relations (shared-parent
		 * siblings, uncles/aunts, cousins) are inferred rather than recorded, and drawing all of them at
		 * once buries the lineage on any tree of real size, so they are shown one node at a time: whichever
		 * node the reader is pointing at, or failing that the selected one.
		 */
		const [activeEntityId, setActiveEntityId] = useState<string | null>(null);
		const revealedEntityId = activeEntityId ?? selectedEntityId ?? null;

		const onNodeMouseEnter = useCallback(
			(event: React.MouseEvent, node: CanvasNode): void => setActiveEntityId(node.id),
			[]
		);
		const onNodeMouseLeave = useCallback((): void => setActiveEntityId(null), []);

		// Keyboard equivalent of hovering: tabbing onto a node reveals its derived relations too, so the
		// reveal is not mouse-only. Bound to the wrapper rather than declared in JSX for the same reason
		// as the keyboard traversal below - the events belong to the focusable nodes inside it.
		useEffect(() => {
			const wrapper = wrapperRef.current;
			const onFocusIn = (event: FocusEvent): void => {
				const node = event.target instanceof Element ? event.target.closest(".react-flow__node") : null;
				setActiveEntityId(node?.getAttribute("data-id") ?? null);
			};
			const onFocusOut = (): void => setActiveEntityId(null);

			wrapper?.addEventListener("focusin", onFocusIn);
			wrapper?.addEventListener("focusout", onFocusOut);

			return () => {
				wrapper?.removeEventListener("focusin", onFocusIn);
				wrapper?.removeEventListener("focusout", onFocusOut);
			};
		}, []);

		const visibleEdges = useMemo(
			() =>
				edges.map((edge: FlowEdge) => {
					const shown =
						edge.data?.derived === true
							? { ...edge, hidden: edge.source !== revealedEntityId && edge.target !== revealedEntityId }
							: edge;
					// With a ring open, only the owner's own connectors and its spokes stay at full strength.
					const isDimmed = ringFocusId !== null && edge.source !== ringFocusId && edge.target !== ringFocusId;

					return isDimmed ? { ...shown, className: styles.dimmedEdge } : shown;
				}),
			[edges, revealedEntityId, ringFocusId]
		);

		// Selection highlight and ring dimming are applied in place so they never reset dragged positions.
		useEffect(() => {
			const dimmed = (id: string): string | undefined =>
				ringFocusId !== null && id !== ringFocusId ? styles.dimmedNode : undefined;

			setNodes((current: CanvasNode[]) =>
				current.map((node: CanvasNode) => {
					if (node.type === "entity") {
						return {
							...node,
							className: dimmed(node.id),
							data: { ...(node.data as EntityNodeData), isSelected: node.id === selectedEntityId }
						} as CanvasNode;
					}
					if (node.type === "linkedTree") {
						return { ...node, className: dimmed(node.id) };
					}
					if (node.type === "satellite") {
						const data = node.data as SatelliteNodeData;

						return {
							...node,
							data: { ...data, isSelected: data.entity !== null && data.entity.id === selectedEntityId }
						} as CanvasNode;
					}

					return node;
				})
			);
		}, [ringFocusId, selectedEntityId, setNodes]);

		// Re-fit the viewport whenever the layout mode or radial center changes (the fitView prop only
		// applies on init). The animation frame defers past the node rebuild committed in the same pass.
		//
		// hasNodes, not flowInstance alone: onInit can land before the nodes exist, and fitting an
		// empty canvas parks the viewport at the origin with nothing left to re-fit it. The nodes then
		// arrive at their laid-out coordinates - which run to several thousand pixels either side of
		// zero - and sit outside the frame, so the canvas looks empty. Whichever of the two won the
		// race decided whether the tree appeared, which is why it only failed some of the time.
		const hasNodes = nodes.length > 0;
		useEffect(() => {
			if (!flowInstance || !hasNodes) {
				return undefined;
			}

			const frame = window.requestAnimationFrame((): void => {
				flowInstance.fitView({ duration: 400, maxZoom: 1, padding: 0.15 }).catch(() => undefined);
			});

			return () => window.cancelAnimationFrame(frame);
		}, [flowInstance, hasNodes, layoutMode, radialCenterId]);

		/**
		 * Bring a newly created node into the frame when its seat happens to fall outside it. The
		 * anchor it was seated beside is normally in plain view - it is what the user was just working
		 * on - but an anchor sitting hard against an edge leaves its new neighbour just over that edge,
		 * and the canvas is not re-fitted when entities change (that would throw away the pan and zoom
		 * the user chose). The pan keeps the current zoom, and runs at most once per created node, so
		 * it can never drag the viewport back after the user has moved on.
		 */
		const newestSeatId = newEntitySeats.length > 0 ? newEntitySeats[newEntitySeats.length - 1].entityId : null;
		const revealedSeatRef = useRef<string | null>(null);
		useEffect(() => {
			const wrapper = wrapperRef.current;
			if (!flowInstance || !wrapper || newestSeatId === null || revealedSeatRef.current === newestSeatId) {
				return;
			}

			// The entity is still being refetched; the next layout carries its seat.
			const seat = layout.positions.get(newestSeatId);
			if (seat === undefined) {
				return;
			}

			revealedSeatRef.current = newestSeatId;
			const { x, y, zoom } = flowInstance.getViewport();
			const left = -x / zoom;
			const top = -y / zoom;
			const isVisible =
				seat.x >= left &&
				seat.y >= top &&
				seat.x + NODE_WIDTH <= left + wrapper.clientWidth / zoom &&
				seat.y + NODE_HEIGHT <= top + wrapper.clientHeight / zoom;
			if (!isVisible) {
				flowInstance
					.setCenter(seat.x + NODE_WIDTH / 2, seat.y + NODE_HEIGHT / 2, { duration: 400, zoom })
					.catch(() => undefined);
			}
		}, [flowInstance, layout, newestSeatId]);

		const navigate = useNavigate();
		const onNodeClick = useCallback(
			(event: React.MouseEvent, node: CanvasNode): void => {
				if (node.type === "linkedTree") {
					const data = node.data as LinkedTreeNodeData;
					// In edit mode, select the owning entity so the link can be managed from its Edit dialog,
					// instead of navigating away to the linked tree.
					if (!readOnly) {
						const owner = entities.find((item: EntityDto) => item.id === data.ownerEntityId);
						if (owner) {
							onSelectEntity?.(owner);

							return;
						}
					}

					navigate(getTreeViewPath(data.treeId));

					return;
				}

				if (node.type === "satellite") {
					// A chip opens its entity; "+N more" opens the owner, whose details list every relation.
					const { entity, ownerId } = node.data as SatelliteNodeData;
					const target = entity ?? entities.find((item: EntityDto) => item.id === ownerId);
					if (target) {
						onSelectEntity?.(target);
					}

					return;
				}

				onSelectEntity?.((node.data as EntityNodeData).entity);
			},
			[entities, navigate, onSelectEntity, readOnly]
		);

		const onPaneClick = useCallback((): void => {
			onBackgroundClick?.();
		}, [onBackgroundClick]);

		/**
		 * Down/up lookups for keyboard traversal. "Down" is any related entity a generation below and
		 * "up" a generation above, which is what the canvas already draws: succession, membership and
		 * the like place entities on rows just as parenthood does. Reading only Father/Mother edges
		 * would leave a tree of friendships or lore with nothing to traverse.
		 */
		const lineage = useMemo(() => {
			const down = new Map<string, string[]>();
			const up = new Map<string, string[]>();
			layout.adjacency.forEach((neighbours: string[], id: string) => {
				const ownLevel = layout.level.get(id);
				if (ownLevel === undefined) {
					return;
				}

				// Nearest horizontally first, so a step lands on the relative sitting closest on the
				// canvas and stepping back usually returns the way you came.
				const ownX = layout.positions.get(id)?.x ?? 0;
				const distance = (otherId: string): number => Math.abs((layout.positions.get(otherId)?.x ?? 0) - ownX);
				const sorted = [...new Set(neighbours)].sort((a: string, b: string) => distance(a) - distance(b));
				sorted.forEach((otherId: string) => {
					const otherLevel = layout.level.get(otherId);
					if (otherLevel === undefined) {
						return;
					}

					if (otherLevel > ownLevel) {
						down.set(id, [...(down.get(id) ?? []), otherId]);
					} else if (otherLevel < ownLevel) {
						up.set(id, [...(up.get(id) ?? []), otherId]);
					}
				});
			});

			return { children: down, parents: up };
		}, [layout]);

		// The canvas's text alternative: generations in order, entities left to right within each,
		// and the relation phrasing already used on the edges.
		const outline = useMemo(() => {
			const relationsById = new Map<string, string[]>();
			const add = (id: string, text?: string): void => {
				if (text !== undefined) {
					relationsById.set(id, [...(relationsById.get(id) ?? []), text]);
				}
			};
			layout.hierarchical.forEach((edge: Edge) => add(edge.parentId, edge.tooltip));
			layout.badged.forEach((edge: BadgedEdge) => add(edge.aId, edge.tooltip));
			layout.threads.forEach((edge: ThreadEdge) => add(edge.aId, edge.tooltip));
			// Ring entities are not drawn until their owner is selected, so the text alternative reads
			// them out under their owners instead - nothing is hidden from a screen reader.
			split.satellitesByOwner.forEach((satellites: Satellite[], ownerId: string) =>
				satellites.forEach((satellite: Satellite) =>
					add(ownerId, relationTooltip(satellite.relation, allTitleById))
				)
			);

			return canvasEntities
				.map((entity: EntityDto) => ({
					id: entity.id,
					title: entity.title,
					type: entity.type,
					generation: layout.level.get(entity.id) ?? 0,
					relations: relationsById.get(entity.id) ?? []
				}))
				.sort(
					(a: TreeOutlineEntry, b: TreeOutlineEntry) =>
						a.generation - b.generation ||
						(layout.positions.get(a.id)?.x ?? 0) - (layout.positions.get(b.id)?.x ?? 0)
				);
		}, [allTitleById, canvasEntities, layout, split]);

		/** Read by the key handler, which is bound once per lineage rather than on every node change. */
		const nodesRef = useRef(nodes);
		nodesRef.current = nodes;

		/**
		 * Tab walks down to a child, Shift+Tab back up to a parent, rather than through every node
		 * and edge in document order. Entering the canvas with nothing focused lands on whichever
		 * entity is nearest the middle of the view. When there is no relative in that direction the
		 * key is left alone, so a leaf or a root is always a way out and the canvas never traps focus.
		 * With a ring open, Tab steps through its chips first; Enter opens a focused chip, and Escape
		 * closes the ring as clicking empty canvas would.
		 */
		const onCanvasKeyDown = useCallback(
			(event: KeyboardEvent): void => {
				const wrapper = wrapperRef.current;
				if (!wrapper) {
					return;
				}

				// Escape closes the ring the same way clicking empty canvas does.
				if (event.key === "Escape") {
					if (selectedEntityId) {
						event.preventDefault();
						onBackgroundClick?.();
					}

					return;
				}

				// Enter on a focused chip opens it, as a click would.
				if (event.key === "Enter") {
					const focused =
						document.activeElement instanceof Element
							? document.activeElement.closest(".react-flow__node")
							: null;
					const chip = nodesRef.current.find(
						(node: CanvasNode) => node.type === "satellite" && node.id === focused?.getAttribute("data-id")
					);
					if (chip !== undefined) {
						event.preventDefault();
						onNodeClick(event as unknown as React.MouseEvent, chip);
					}

					return;
				}

				if (event.key !== "Tab") {
					return;
				}

				const focusNode = (id: string): boolean => {
					const target = wrapper.querySelector<HTMLElement>(`.react-flow__node[data-id="${CSS.escape(id)}"]`);
					if (!target) {
						return false;
					}

					target.focus();

					return true;
				};

				const active = document.activeElement;
				const current = active instanceof Element ? active.closest(".react-flow__node") : null;

				if (current === null) {
					const bounds = wrapper.getBoundingClientRect();
					const centerX = bounds.left + bounds.width / 2;
					const centerY = bounds.top + bounds.height / 2;
					let nearest: HTMLElement | null = null;
					let shortest = Number.POSITIVE_INFINITY;
					wrapper.querySelectorAll<HTMLElement>(".react-flow__node").forEach((node: HTMLElement) => {
						const box = node.getBoundingClientRect();
						const distance = Math.hypot(
							box.left + box.width / 2 - centerX,
							box.top + box.height / 2 - centerY
						);
						if (distance < shortest) {
							shortest = distance;
							nearest = node;
						}
					});
					if (nearest !== null) {
						event.preventDefault();
						(nearest as HTMLElement).focus();
					}

					return;
				}

				const id = current.getAttribute("data-id");
				// With a ring open, Tab from its owner steps through the chips in seat order, and on past
				// the last one to the owner's own next relative; Shift+Tab walks back to the owner.
				const chipIndex = id === null ? -1 : ringChipIds.indexOf(id);
				let next: string | undefined;
				if (ringOwnerId !== null && id === ringOwnerId && !event.shiftKey && ringChipIds.length > 0) {
					[next] = ringChipIds;
				} else if (ringOwnerId !== null && chipIndex >= 0) {
					if (event.shiftKey) {
						next = chipIndex === 0 ? ringOwnerId : ringChipIds[chipIndex - 1];
					} else {
						next = ringChipIds[chipIndex + 1] ?? lineage.children.get(ringOwnerId)?.[0];
					}
				} else if (id !== null) {
					next = (event.shiftKey ? lineage.parents : lineage.children).get(id)?.[0];
				}
				if (next !== undefined && focusNode(next)) {
					event.preventDefault();
				}
			},
			[lineage, onBackgroundClick, onNodeClick, ringChipIds, ringOwnerId, selectedEntityId]
		);

		// Bound to the element rather than declared in JSX: the wrapper is a plain container, and the
		// keys it handles belong to the focusable nodes inside it, not to the div itself.
		useEffect(() => {
			const wrapper = wrapperRef.current;
			wrapper?.addEventListener("keydown", onCanvasKeyDown);

			return () => wrapper?.removeEventListener("keydown", onCanvasKeyDown);
		}, [onCanvasKeyDown]);

		return (
			<div ref={wrapperRef} className={styles.graphWrapper}>
				<TreeOutline entries={outline} />
				{!isCanvasReady && (
					<div className={styles.canvasCover}>
						<Spinner label={t("view.loadingCanvas")} />
					</div>
				)}
				<ReactFlow
					edgeTypes={EDGE_TYPES}
					edges={visibleEdges}
					fitViewOptions={{ maxZoom: 1, padding: 0.15 }}
					maxZoom={2.5}
					minZoom={0.15}
					nodeTypes={NODE_TYPES}
					nodes={nodes}
					nodesConnectable={false}
					nodesDraggable={!readOnly}
					onEdgesChange={onEdgesChange}
					onInit={setFlowInstance}
					onNodeClick={onNodeClick}
					onNodeDragStart={onNodeDragStart}
					onNodeDragStop={onNodeDragStop}
					onNodeMouseEnter={onNodeMouseEnter}
					onNodeMouseLeave={onNodeMouseLeave}
					onNodesChange={onNodesChange}
					onPaneClick={onPaneClick}
					proOptions={{ hideAttribution: true }}
					fitView>
					<EdgePublisher edges={flowEdges} onPublish={onPublishEdges} />
					<Background color="rgba(200, 168, 98, 0.55)" gap={28} size={1.6} />
					<Controls showInteractive={false}>
						{showMiniMap && (
							<ControlButton
								aria-label={isMiniMapVisible ? t("view.hideMiniMap") : t("view.showMiniMap")}
								aria-pressed={isMiniMapVisible}
								onClick={toggleMiniMap}
								title={isMiniMapVisible ? t("view.hideMiniMap") : t("view.showMiniMap")}>
								<svg
									fill="none"
									stroke="currentColor"
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									viewBox="0 0 24 24">
									<path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2ZM9 4v14M15 6v14" />
								</svg>
							</ControlButton>
						)}
					</Controls>
					{showMiniMap && isMiniMapVisible && (
						<MiniMap
							className={styles.miniMap}
							maskColor="rgba(31, 51, 41, 0.14)"
							nodeComponent={MiniMapNode}
							position="bottom-left"
							pannable
							zoomable
						/>
					)}
				</ReactFlow>
			</div>
		);
	}
);

TreeGraph.displayName = "TreeGraph";

export default TreeGraph;
